import { useState } from 'react';
import { Input, Select, DatePicker, Avatar, Tooltip, Popover } from 'antd';
import { SearchOutlined, FilterOutlined, CloseCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import styles from './FeedFilterBar.module.scss';

const { RangePicker } = DatePicker;

export default function FeedFilterBar({ filters, onChange, assignes = [], priorites = [], statuts = [] }) {
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const MAX_VISIBLE = 5;

  const setFilter = (key, val) => onChange({ ...filters, [key]: val ?? null });

  const toggleUser = (userId) =>
    setFilter('responsable_id', filters.responsable_id === userId ? null : userId);

  const filtered = assignes.filter(u =>
    !assigneeSearch || u.name?.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const visible  = assignes.slice(0, MAX_VISIBLE);
  const overflow = assignes.length - MAX_VISIBLE;

  const dateRange = (filters.date_from && filters.date_to)
    ? [dayjs(filters.date_from), dayjs(filters.date_to)]
    : null;

  const handleDateRange = (dates) => {
    onChange({
      ...filters,
      date_from: dates?.[0]?.format('YYYY-MM-DD') ?? null,
      date_to:   dates?.[1]?.format('YYYY-MM-DD') ?? null,
    });
  };

  const hasActiveFilters = !!(filters.responsable_id || filters.type || filters.priorite_id
    || filters.statut_id || filters.date_from || filters.date_to);

  const clearAll = () => onChange({ searchText: filters.searchText });

  // Popover content: searchable user list
  const popoverContent = (
    <div className={styles.popoverList}>
      <Input
        prefix={<SearchOutlined />}
        placeholder="Rechercher..."
        value={assigneeSearch}
        onChange={e => setAssigneeSearch(e.target.value)}
        size="small"
        style={{ marginBottom: 8 }}
        allowClear
      />
      {filtered.map(u => (
        <div
          key={u.id}
          className={`${styles.popoverItem} ${filters.responsable_id === u.id ? styles.popoverItemActive : ''}`}
          onClick={() => { toggleUser(u.id); setPopoverOpen(false); }}
        >
          <Avatar size={26} src={u.avatar}>{u.name?.[0]}</Avatar>
          <span className={styles.popoverName}>{u.name}</span>
          {filters.responsable_id === u.id && <CloseCircleFilled className={styles.popoverCheck} />}
        </div>
      ))}
      {filtered.length === 0 && <div className={styles.popoverEmpty}>Aucun résultat</div>}
    </div>
  );

  return (
    <div className={styles.bar}>
      {/* Search */}
      <Input
        prefix={<SearchOutlined />}
        placeholder="Rechercher..."
        value={filters.searchText ?? ''}
        onChange={e => setFilter('searchText', e.target.value || null)}
        allowClear
        className={styles.search}
      />

      {/* Avatar group */}
      {assignes.length > 0 && (
        <div className={styles.avatarGroup}>
          <Tooltip title="Tous">
            <Avatar
              size={32}
              className={`${styles.avatar} ${!filters.responsable_id ? styles.avatarActive : ''}`}
              onClick={() => setFilter('responsable_id', null)}
            >
              <FilterOutlined style={{ fontSize: 13 }} />
            </Avatar>
          </Tooltip>

          {visible.map(u => (
            <Tooltip key={u.id} title={u.name}>
              <Avatar
                size={32}
                src={u.avatar}
                className={`${styles.avatar} ${filters.responsable_id === u.id ? styles.avatarActive : ''}`}
                onClick={() => toggleUser(u.id)}
              >
                {u.name?.[0]}
              </Avatar>
            </Tooltip>
          ))}

          {overflow > 0 && (
            <Popover
              content={popoverContent}
              title="Filtrer par collaborateur"
              trigger="click"
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              placement="bottomLeft"
            >
              <Avatar
                size={32}
                className={`${styles.avatar} ${styles.avatarMore}`}
              >
                +{overflow}
              </Avatar>
            </Popover>
          )}
        </div>
      )}

      {/* Type */}
      <Select
        value={filters.type ?? null}
        onChange={v => setFilter('type', v)}
        placeholder="Type"
        allowClear
        style={{ width: 120 }}
        options={[
          { value: 'affaire', label: 'Dossiers' },
          { value: 'tache',   label: 'Tâches' },
        ]}
      />

      {/* Date range */}
      <RangePicker
        value={dateRange}
        onChange={handleDateRange}
        format="DD/MM/YYYY"
        placeholder={['Date du', 'au']}
        allowClear
        style={{ width: 220 }}
      />

      {/* Priorité */}
      {priorites.length > 0 && (
        <Select
          value={filters.priorite_id ?? null}
          onChange={v => setFilter('priorite_id', v)}
          placeholder="Priorité"
          allowClear
          style={{ width: 130 }}
          options={priorites.map(p => ({ value: p.id, label: p.nom }))}
        />
      )}

      {/* Statut (tâches) */}
      {statuts.length > 0 && (
        <Select
          value={filters.statut_id ?? null}
          onChange={v => setFilter('statut_id', v)}
          placeholder="Statut"
          allowClear
          style={{ width: 130 }}
          options={statuts.map(s => ({ value: s.id, label: s.nom }))}
        />
      )}

      {/* Clear all */}
      {hasActiveFilters && (
        <span className={styles.clearAll} onClick={clearAll}>
          <CloseCircleFilled /> Tout effacer
        </span>
      )}
    </div>
  );
}

FeedFilterBar.propTypes = {
  filters:   PropTypes.object.isRequired,
  onChange:  PropTypes.func.isRequired,
  assignes:  PropTypes.array,
  priorites: PropTypes.array,
  statuts:   PropTypes.array,
};
